/**
 * VendaSummaryCards Component - Cards de Resumo de Vendas
 */

import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TrendingUp, Calendar, CalendarRange, CalendarDays } from "lucide-react";
import { Venda } from "@/types/venda";
import { formatCurrency } from "@/lib/utils";
import { isToday, isThisWeek, isThisMonth, isThisYear } from "date-fns";

interface VendaSummaryCardsProps {
  vendas: Venda[];
}

export function VendaSummaryCards({ vendas }: VendaSummaryCardsProps) {
  const summaries = useMemo(() => {
    const hoje = vendas.filter((v) => isToday(new Date(v.data_venda)));
    const semana = vendas.filter((v) => isThisWeek(new Date(v.data_venda), { weekStartsOn: 0 }));
    const mes = vendas.filter((v) => isThisMonth(new Date(v.data_venda)));
    const ano = vendas.filter((v) => isThisYear(new Date(v.data_venda)));

    return [
      {
        title: "Vendas Hoje",
        icon: TrendingUp,
        quantidade: hoje.length,
        valor: hoje.reduce((sum, v) => sum + v.valor_total, 0),
        iconColor: "text-primary",
      },
      {
        title: "Vendas na Semana",
        icon: Calendar,
        quantidade: semana.length,
        valor: semana.reduce((sum, v) => sum + v.valor_total, 0),
        iconColor: "text-blue-500",
      },
      {
        title: "Vendas no Mês",
        icon: CalendarRange,
        quantidade: mes.length,
        valor: mes.reduce((sum, v) => sum + v.valor_total, 0),
        iconColor: "text-green-500",
      },
      {
        title: "Vendas no Ano",
        icon: CalendarDays,
        quantidade: ano.length,
        valor: ano.reduce((sum, v) => sum + v.valor_total, 0),
        iconColor: "text-purple-500",
      },
    ];
  }, [vendas]);

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      {summaries.map((summary) => {
        const Icon = summary.icon;
        return (
          <Card key={summary.title}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {summary.title}
              </CardTitle>
              <Icon className={`h-4 w-4 ${summary.iconColor}`} />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{summary.quantidade}</div>
              <p className="text-xs text-muted-foreground">
                {formatCurrency(summary.valor)}
              </p>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
